import { motion } from "framer-motion";
import { Check, Sparkles } from "lucide-react";
import { Reveal } from "./Reveal";
import { getIcon } from "../lib/icons";
import { mediaUrl } from "../lib/api";

const accentText = { blue: "text-glg-blue", orange: "text-glg-orange", green: "text-glg-green" };
const accentBg = { blue: "bg-glg-blue/10", orange: "bg-glg-orange/10", green: "bg-glg-green/10" };

export const TechGLG = ({ software = [] }) => {
  if (!software.length) return null;
  return (
    <section id="software" data-testid="tech-section" className="relative overflow-hidden bg-glg-light py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="mb-14 max-w-2xl">
          <Reveal>
            <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.3em] text-glg-blue">
              <Sparkles className="h-4 w-4" /> Tecnología desarrollada por GLG
            </p>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-4 font-heading text-3xl font-semibold leading-tight tracking-tight text-glg-black sm:text-5xl">
              Software propio que impulsa tu operación
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-5 text-base leading-relaxed text-black/55">
              No solo instalamos e integramos: también desarrollamos nuestras propias plataformas.
            </p>
          </Reveal>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {software.map((sw, i) => {
            const Icon = getIcon(sw.icon);
            const accent = sw.accent || "blue";
            return (
              <motion.div
                key={sw.id || i}
                data-testid={`software-card-${i}`}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="group relative overflow-hidden rounded-3xl border border-black/5 bg-white p-8 shadow-sm transition-shadow duration-300 hover:shadow-xl sm:p-10"
              >
                <div className={`mb-6 inline-flex h-14 w-14 items-center justify-center rounded-2xl ${accentBg[accent]}`}>
                  <Icon className={`h-7 w-7 ${accentText[accent]}`} />
                </div>
                <h3 className="font-heading text-2xl font-bold tracking-tight text-glg-black">{sw.name}</h3>
                <p className={`mt-1 text-sm font-medium ${accentText[accent]}`}>{sw.tagline}</p>
                <p className="mt-4 text-sm leading-relaxed text-black/55">{sw.description}</p>

                {sw.image && (
                  <div className="mt-6 overflow-hidden rounded-2xl border border-black/5">
                    <img src={mediaUrl(sw.image)} alt={sw.name} loading="lazy" className="w-full object-cover" />
                  </div>
                )}

                {(sw.features || []).length > 0 && (
                  <ul className="mt-6 grid gap-2.5 sm:grid-cols-2">
                    {sw.features.map((f) => (
                      <li key={f} className="flex items-center gap-2 text-sm text-black/70">
                        <Check className={`h-4 w-4 ${accentText[accent]}`} />
                        {f}
                      </li>
                    ))}
                  </ul>
                )}

                {sw.link && (
                  <a
                    href={sw.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`mt-7 inline-block text-sm font-semibold ${accentText[accent]}`}
                  >
                    Ver más →
                  </a>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
